import { useState, useCallback } from "react";
import * as d3 from "d3";

export const useZoom = (
  diagramRef,
  viewBoxWidth,
  viewBoxHeight,
  minScale = 0.1,
  maxScale = 10
) => {
  const [currentScale, setCurrentScale] = useState(1);

  const applyZoom = (scale) => {
    const svg = d3.select(diagramRef.current).select("svg");
    svg.call(zoomBehavior.transform, d3.zoomIdentity.scale(scale));
  };

  function zoomed(event) {
    const host = diagramRef.current;
    if (!host) return;
    const svg = d3.select(host).select("svg");
    // Prefer the first direct child <g> as the content root
    const g = svg.select(":scope > g");
    g.attr("transform", event.transform);
    setCurrentScale(event.transform.k);
  }

  const zoomBehavior = d3
    .zoom()
    .extent([
      [0, 0],
      [viewBoxWidth, viewBoxHeight],
    ])
    .scaleExtent([minScale, maxScale])
    .on("zoom", zoomed);

  const zoomIn = () => {
    const svgElement = d3.select(diagramRef.current).select("svg");
    zoomBehavior.scaleBy(svgElement.transition().duration(200), 1.1);
    const newScale = currentScale * 1.1;
    setCurrentScale(newScale);
    return newScale;
  };

  const zoomOut = () => {
    const svgElement = d3.select(diagramRef.current).select("svg");
    zoomBehavior.scaleBy(svgElement.transition().duration(200), 0.9);
    const newScale = currentScale * 0.9;
    setCurrentScale(newScale);
    return newScale;
  };

  const resetZoom = () => {
    const svgElement = d3.select(diagramRef.current).select("svg");
    svgElement
      .transition()
      .duration(300)
      .call(zoomBehavior.transform, d3.zoomIdentity);
    setCurrentScale(1);
    return 1; // Reset scale to 1
  };

  function zoomToFit({ padding = 16, duration = 350 } = {}) {
    const host = diagramRef.current;
    if (!host) return;

    const svg = d3.select(host).select("svg");
    // Measure the content bounds (inner g)
    const g = svg.select(":scope > g");
    const box = g.node()?.getBBox();
    const width = host.clientWidth;
    const height = host.clientHeight;
    if (!box || !box.width || !box.height || !width || !height) return;

    // Compute fit scale (don’t clamp to minScale here; allow smaller if needed)
    const scaleX = (width - padding * 2) / box.width;
    const scaleY = (height - padding * 2) / box.height;
    const fitScale = Math.min(scaleX, scaleY);
    const k = Math.min(maxScale, fitScale); // only cap at max

    // Center the content: translate(center) • scale(k) • translate(-contentCenter)
    const cx = box.x + box.width / 2;
    const cy = box.y + box.height / 2;

    const transform = d3.zoomIdentity
      .translate(width / 2, height / 2)
      .scale(k)
      .translate(-cx, -cy);

    // Apply to the element that has the zoom behavior (svg),
    // which will call zoomed() and move the inner g.
    svg.transition().duration(duration).call(zoomBehavior.transform, transform);

    setCurrentScale(k);
    return k;
  }

  const setScale = (scale) => {
    applyZoom(scale);
    setCurrentScale(scale);
    return scale;
  };

  return {
    zoomIn,
    zoomOut,
    resetZoom,
    zoomToFit,
    zoomBehavior,
    setScale,
    currentScale,
  };
};

export const useDragBehavior = (handleMove) => {
  // Higher-order functions to create drag behavior
  const dragBehavior = useCallback(() => {
    let offsetX, offsetY;
    let dragStartPoint = null;

    function dragstarted(event) {
      dragStartPoint = { x: event.x, y: event.y };

      const el = d3.select(this);

      if (el.classed("label")) {
        // Get the current position of the label
        const currentX = parseFloat(el.attr("x")) || 0;
        const currentY = parseFloat(el.attr("y")) || 0;

        // Calculate simple offsets
        offsetX = event.x - currentX;
        offsetY = event.y - currentY;
      } else {
        // For roles and threads, use the transform attribute
        let transform = el.attr("transform");
        let translate = transform
          ? transform.substring(10, transform.length - 1).split(",")
          : [0, 0];
        translate = translate.map((d) => +d);

        offsetX = event.x - translate[0];
        offsetY = event.y - translate[1];
      }

      el.raise();
    }

    function dragged(event) {
      const el = d3.select(this);

      if (
        el.classed("label") ||
        el.classed("condition-label") ||
        el.classed("repeat-label")
      ) {
        // Calculate the new position for the label using simple offset
        const newX = event.x - offsetX;
        const newY = event.y - offsetY;

        // Update the label's position
        el.attr("x", newX).attr("y", newY);
      } else if (el.classed("role-group") || el.classed("thread-group")) {
        // Calculate the new position for roles and threads using simple offset
        console.log("Dragging role or thread group");
        const newX = event.x - offsetX;
        const newY = event.y - offsetY;

        // Update the group's position using the transform attribute
        el.attr("transform", `translate(${newX}, ${newY})`);
      }
    }

    function dragended(event) {
      const distance = Math.hypot(
        event.x - dragStartPoint.x,
        event.y - dragStartPoint.y
      );

      if (distance < 5) {
        // Threshold in pixels to consider it a click instead of a drag
        return;
      }

      const el = d3.select(this);

      // Roles, Threads and Labels can be dragged
      if (
        el.classed("label") ||
        el.classed("condition-label") ||
        el.classed("repeat-label")
      ) {
        // Calculate the new position for the label using simple offset
        const newX = event.x - offsetX;
        const newY = event.y - offsetY;

        // Get the parent group to retrieve the data-id
        const parentGroup = el.node().closest("g");
        const element_id = d3.select(parentGroup).attr("data-id");

        // Update the label's position
        el.attr("x", newX).attr("y", newY);

        // Send the updated position to handleMove
        const type = el.classed("label")
          ? "label"
          : el.classed("condition-label")
            ? "condition-label"
            : "repeat-label";
        handleMove({
          id: element_id,
          type,
          position: { x: newX, y: newY },
        });
      } else if (el.classed("role-group") || el.classed("thread-group")) {
        const newX = Math.max(0, event.x - offsetX);
        const newY = Math.max(0, event.y - offsetY);

        const id = el.attr("data-id");
        const type = el.classed("role-group") ? "role" : "thread";

        handleMove({
          id,
          type,
          position: { x: newX, y: newY },
        });
      } else {
        console.log("dragged element not implemented", el);
      }
    }

    return d3
      .drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  }, [handleMove]);

  // Empty drag function for now
  const dragStateBehaviour = useCallback(() => {
    function dragstarted(event, d) {
      // Since we only want to move the second endpoint,
      // we only need the starting position of the drag for that point.
      const secondEndPoint = d.endPoints[1];
      this.dragOffset = {
        x: event.x - secondEndPoint[0],
        y: event.y - secondEndPoint[1],
      };
      d3.select(this).raise();
    }

    function dragged(event, d) {
      const newX = event.x - this.dragOffset.x;
      const newY = event.y - this.dragOffset.y;
      const firstEndPoint = d.endPoints[0];

      // Update the endpoint in the data 'd'
      d.endPoints[1] = [newX, newY];

      // Since we're dealing with a line, update x2 and y2 attributes
      d3.select(this).select("line").attr("x2", newX).attr("y2", newY);

      // Move the state description
      d3.select(this)
        .select("ellipse")
        .attr("cx", (newX - firstEndPoint[0]) / 2)
        .attr("cy", (newY - firstEndPoint[1]) / 2);

      d3.select(this)
        .select("text")
        .attr("x", (10 + newX - firstEndPoint[0]) / 2)
        .attr("y", (newY - firstEndPoint[1]) / 2);
    }

    function dragended(event, d) {
      const distance = Math.hypot(
        event.x - d.endPoints[1][0],
        event.y - d.endPoints[1][1]
      );

      if (distance < 5) {
        // Threshold in pixels to consider it a click instead of a drag
        return;
      }

      const x = event.sourceEvent.pageX; //sourceEvent.pageX - event.sourceEvent.offsetX + d.x;
      const y = event.sourceEvent.pageY;

      const el = d3.select(this);
      // States can be dragged but only for connection open or end states into other threads within the role
      if (el.classed("state-group")) {
        console.log("drag ended: ", el);
        // look for a valid drop target
        // ... something, something
        const elementAtDrop = document.elementFromPoint(x, y);
        console.log("position at drag state end", x, y);
        console.log("element at drag state end", elementAtDrop);
        const amIaValidDropTarget = elementAtDrop.closest("g.state-group");
        console.log("am I a valid drop target", amIaValidDropTarget);
      } else {
        console.log("dragged element not implemented", el);
      }
    }

    return d3
      .drag()
      .on("start", dragstarted)
      .on("drag", dragged)
      .on("end", dragended);
  }, []);

  return { dragBehavior, dragStateBehaviour };
};
